import React from 'react';
import {
  Container,
  Box,
  Typography,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ErrorIcon from '@mui/icons-material/Error';
import { ProgressBar } from './ProgressBar';

interface WizardLayoutProps {
  /** Whether viewport is mobile */
  isMobile: boolean;
  /** Current page within the section (for mobile pagination back button) */
  sectionPageIndex: number;
  /** Current section index */
  sectionIndex: number;
  /** The current section object (null/undefined when in review step) */
  currentSection: any;
  /** Ref to the scrollable inner box */
  scrollContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  /** Navigate to previous visible section */
  navigateToPrevVisibleSection: () => void;
  /** All sections (for progress bar) */
  sections: any[];
  /** Current scores (for progress bar visibility) */
  scores: Record<string, any>;
  /** Generic wizard review step flag — hides regular section header, shows review header */
  isReviewStep?: boolean;
  /** When set, back navigation is hidden for sections at or before this index */
  minSectionIndex?: number | null;
  /** When true, renders progress bar and back button in RTL (Hebrew) mode */
  isRTL?: boolean;
  /** When true, hides the logo and uses height: 100% instead of 100vh for embedded rendering */
  isEmbedded?: boolean;
  /** Custom header logo URL (e.g. clinic logo for il_webhook questionnaires); defaults to the Psyara logo */
  logoSrc?: string;
  /** Optional second header logo (e.g. the referral's logo), shown on the physical right */
  rightLogoSrc?: string;
  /** Main content to render inside the scrollable area (below section title) */
  children: React.ReactNode;
}

/**
 * Shared outer shell for both the public questionnaire wizard and the generic
 * questionnaire wizard. Renders the Psyara logo header, the white wrapper,
 * the scrollable inner box with custom scrollbar, back navigation buttons,
 * progress bar, and section title / description / alert banner.
 */
export const WizardLayout: React.FC<WizardLayoutProps> = ({
  isMobile,
  sectionPageIndex,
  sectionIndex,
  currentSection,
  scrollContainerRef,
  navigateToPrevVisibleSection,
  sections,
  scores,
  isReviewStep = false,
  minSectionIndex = null,
  isRTL = false,
  isEmbedded = false,
  logoSrc,
  rightLogoSrc,
  children,
}) => {
  return (
    <Box
      sx={{
        height: isEmbedded ? '100%' : '100vh',
        backgroundColor: '#E5F0F1',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* ── Psyara Logo Header ──────────────────────────────────────────────── */}
      {!isEmbedded && (
      <Box
        // Force LTR so the logo stays on the physical left in Hebrew (RTL) mode
        dir={isRTL ? 'ltr' : undefined}
        sx={{
          backgroundColor: '#E5F0F1',
          p: { xs: 0.5, sm: 0.75, md: 1 },
          pl: { xs: isRTL ? 1 : 0, sm: isRTL ? 1 : 0, md: 3 },
          borderBottom: '1px solid #E0E0E0',
          flexShrink: 0,
          display: isRTL ? 'flex' : { xs: 'flex', md: 'block' },
          justifyContent: isRTL ? 'flex-start' : { xs: 'center', md: 'flex-start' },
          position: 'relative',
        }}
      >
        {/* Back Button - Mobile (in header) */}
        {isMobile && (sectionPageIndex > 0 || (currentSection?.showBackButton && sectionIndex > 0)) && (minSectionIndex === null || sectionIndex > minSectionIndex) && (
          <Box
            onClick={navigateToPrevVisibleSection}
            sx={{
              position: 'absolute',
              ...(isRTL ? { right: { xs: 8, sm: 8 } } : { left: { xs: 8, sm: 8 } }),
              top: '50%',
              transform: isRTL ? 'translateY(-50%) scaleX(-1)' : 'translateY(-50%)',
              width: { xs: 27, sm: 27 },
              height: { xs: 27, sm: 27 },
              cursor: 'pointer',
              backgroundImage: 'url(/images/back-icon.png)',
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              filter: 'invert(40%) sepia(18%) saturate(1458%) hue-rotate(144deg) brightness(90%) contrast(87%)',
              zIndex: 10,
              '&:hover': { opacity: 0.7 },
            }}
          />
        )}
        <Box
          component="img"
          src={logoSrc || '/images/psyara-logo.png'}
          alt="Psyara"
          sx={{ height: { xs: 28, sm: 32, md: 45 }, width: 'auto' }}
        />
        {/* Referral logo — physical right, opposite the clinic/Psyara logo */}
        {rightLogoSrc && (
          <Box
            component="img"
            src={rightLogoSrc}
            alt="Referral"
            sx={{
              position: 'absolute',
              right: { xs: 8, sm: 12, md: 24 },
              top: '50%',
              transform: 'translateY(-50%)',
              height: { xs: 24, sm: 28, md: 40 },
              width: 'auto',
              maxWidth: { xs: 100, sm: 140, md: 220 },
              objectFit: 'contain',
            }}
          />
        )}
      </Box>
      )}

      {/* ── White Parent Wrapper ────────────────────────────────────────────── */}
      <Box
        sx={{
          backgroundColor: 'white',
          flex: 1,
          pt: { xs: 2 },
          pb: { xs: 2, sm: 3, md: 4 },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* ── Inner Scrollable Box ────────────────────────────────────────── */}
        <Box
          ref={scrollContainerRef}
          sx={{
            backgroundColor: '#E5F0F1',
            mx: { xs: 2, sm: 4, md: 6 },
            borderRadius: '16px',
            flex: 1,
            overflowY: 'auto',
            display: { xs: 'flex', md: 'block' },
            flexDirection: { xs: 'column', md: 'column' },
            justifyContent: { xs: 'flex-start', md: 'flex-start' },
            position: 'relative',
            '&::-webkit-scrollbar': {
              width: '7px',
              '@media (min-width: 900px)': { width: '15px' },
            },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#3A7380',
              borderRadius: '15px',
              border: { xs: '1px solid #E5F0F1', md: '2px solid #E5F0F1' },
              minHeight: '40px',
              '&:hover': { backgroundColor: '#2e5c66' },
            },
          }}
        >
          {/* Back Button - Desktop (in scrollable area) */}
          {!isMobile && currentSection?.showBackButton && sectionIndex > 0 && (minSectionIndex === null || sectionIndex > minSectionIndex) && (
            <Box
              onClick={navigateToPrevVisibleSection}
              sx={{
                position: 'absolute',
                top: { xs: 8, sm: 16 },
                ...(isRTL ? { right: { xs: 8, sm: 16 } } : { left: { xs: 8, sm: 16 } }),
                width: { xs: 32, sm: 40 },
                height: { xs: 32, sm: 40 },
                cursor: 'pointer',
                backgroundImage: 'url(/images/back-icon.png)',
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                transform: isRTL ? 'scaleX(-1)' : 'none',
                zIndex: 10,
                '&:hover': { opacity: 0.7 },
              }}
            />
          )}

          {/* Progress Bar */}
          {sections && sections.length > 0 && (
            <Box sx={{ pl: { xs: 0, xl: 0 } }}>
              <ProgressBar
                sections={sections}
                currentSectionIndex={sectionIndex}
                scores={scores}
                isRTL={isRTL}
              />
            </Box>
          )}

          {/* ── Section Title / Description / Alert Banner ─────────────────── */}
          <Container maxWidth="md">
            <Box sx={{ maxWidth: '700px', mx: 'auto' }}>
              {/* Normal section header (hidden in review step) */}
              {!isReviewStep && currentSection && (
                <>
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#3A7380',
                      fontWeight: 700,
                      pt: { xs: 2, sm: 3, md: 6 },
                      pb: { xs: currentSection.description ? 1 : 2, sm: 3, md: currentSection.description ? 1 : 4 },
                      fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                      fontFamily: '"Open Sans", sans-serif',
                      textAlign: { xs: 'center', md: currentSection.titleAlignment || 'center' },
                    }}
                  >
                    {currentSection.title}
                  </Typography>

                  {currentSection.description && (
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#5A7380',
                        fontFamily: '"Open Sans", sans-serif',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        pb: { xs: 1.5, md: 1 },
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        textAlign: { xs: 'center', md: currentSection.titleAlignment || 'center' },
                      }}
                    >
                      {currentSection.description}
                    </Typography>
                  )}

                  {currentSection.alertBanner && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        backgroundColor:
                          currentSection.alertBanner.type === 'warning' ? '#3A7380' :
                          currentSection.alertBanner.type === 'danger' ? '#d32f2f' :
                          '#3AA09A',
                        color: 'white',
                        borderRadius: '20px',
                        minHeight: '40px',
                        px: 2,
                        py: 1,
                        mb: 2,
                      }}
                    >
                      {currentSection.alertBanner.type === 'warning' && (
                        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                          <svg width="17" height="15" viewBox="0 0 17 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0.782083 15C0.636944 15 0.505 14.9619 0.38625 14.8858C0.2675 14.8097 0.175139 14.7089 0.109166 14.5833C0.0431942 14.4578 0.00704141 14.3222 0.000708075 14.1767C-0.00562526 14.0311 0.0305275 13.8889 0.109166 13.75L7.43208 0.416667C7.51125 0.277778 7.61364 0.173611 7.73925 0.104167C7.86486 0.0347222 7.99338 0 8.12479 0C8.25621 0 8.38499 0.0347222 8.51112 0.104167C8.63726 0.173611 8.73939 0.277778 8.8175 0.416667L16.1404 13.75C16.2196 13.8889 16.256 14.0314 16.2497 14.1775C16.2433 14.3236 16.2069 14.4589 16.1404 14.5833C16.0739 14.7078 15.9816 14.8086 15.8633 14.8858C15.7451 14.9631 15.6132 15.0011 15.4675 15H0.782083ZM2.14771 13.3333H14.1019L8.12479 2.5L2.14771 13.3333ZM8.12479 12.5C8.3491 12.5 8.53725 12.42 8.68925 12.26C8.84125 12.1 8.91699 11.9022 8.91646 11.6667C8.91593 11.4311 8.83993 11.2333 8.68846 11.0733C8.53699 10.9133 8.3491 10.8333 8.12479 10.8333C7.90049 10.8333 7.7126 10.9133 7.56113 11.0733C7.40965 11.2333 7.33365 11.4311 7.33313 11.6667C7.3326 11.9022 7.4086 12.1003 7.56113 12.2608C7.71365 12.4214 7.90154 12.5011 8.12479 12.5ZM8.12479 10C8.3491 10 8.53725 9.92 8.68925 9.76C8.84125 9.6 8.91699 9.40222 8.91646 9.16667V6.66667C8.91646 6.43056 8.84046 6.23278 8.68846 6.07333C8.53646 5.91389 8.34857 5.83389 8.12479 5.83333C7.90101 5.83278 7.71313 5.91278 7.56113 6.07333C7.40913 6.23389 7.33313 6.43167 7.33313 6.66667V9.16667C7.33313 9.40278 7.40913 9.60083 7.56113 9.76083C7.71313 9.92083 7.90101 10.0006 8.12479 10Z" fill="white"/>
                          </svg>
                        </Box>
                      )}
                      {currentSection.alertBanner.type === 'danger' && (
                        <ErrorIcon sx={{ fontSize: 24, flexShrink: 0 }} />
                      )}
                      {currentSection.alertBanner.type === 'info' && (
                        <InfoIcon sx={{ fontSize: 24, flexShrink: 0 }} />
                      )}
                      <Typography
                        sx={{
                          fontFamily: '"Open Sans", sans-serif',
                          fontSize: '0.875rem',
                          fontWeight: 400,
                          lineHeight: 1.4,
                        }}
                      >
                        {currentSection.alertBanner.content}
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              {/* Review step header (Generic wizard only) */}
              {isReviewStep && (
                <>
                  <Typography
                    variant="h4"
                    align="center"
                    sx={{
                      color: '#3A7380',
                      fontWeight: 700,
                      pt: { xs: 3, sm: 4, md: 6 },
                      pb: 1,
                      fontSize: { xs: '1.5rem', md: '1.5rem' },
                      fontFamily: '"Open Sans", sans-serif',
                    }}
                  >
                    Review Your Answers
                  </Typography>
                  <Typography
                    variant="body1"
                    align="center"
                    sx={{
                      color: '#5A7380',
                      fontFamily: '"Open Sans", sans-serif',
                      pb: 1,
                    }}
                  >
                    Please review all your answers before submitting
                  </Typography>
                </>
              )}
            </Box>
          </Container>

          {/* ── Main Content (passed as children) ─────────────────────────── */}
          {children}
        </Box>
      </Box>
    </Box>
  );
};
